'use client';
import React, { startTransition } from 'react';

import { SessionContext } from '@benzinga/session-context';
import { BasicNewsManager, BreakingNews } from '@benzinga/basic-news-manager';

import { HeaderMobile } from './HeaderMobile';
import { HeaderDesktop, type HeaderDesktopProps } from './HeaderDesktop';
import { DefaultQuotesList, featuredMenu } from './_mockMenu';
import type { HeaderMobileProps } from './HeaderMobile';

import { appEnvironment, appName } from '@benzinga/utils';
import { DateTime } from 'luxon';
import { useRecentTickers } from '@benzinga/hooks';

import { NavigationHeaderProps } from '../../entities';
import { shouldLoadHeaderDisplayAd } from '../../utils/estimateDesktopStickyNavOffset';

import { SimpleHeaderDesktop } from './Variants/Simple/SimpleHeader';
import { SimpleHeaderMobile } from './Variants/Simple/SimpleHeaderMobile';
import { DelayedQuote, QuotesManager } from '@benzinga/quotes-manager';
import { StockSymbol } from '@benzinga/session';

// const InvestingChannel = React.lazy(() =>
//   import('@benzinga/ads').then(module => {
//     return { default: module.InvestingChannel };
//   }),
// );

export const HeaderNavigationComponent: React.FC<HeaderDesktopProps> = ({ variant, ...props }) => {
  const HeadersVariants = {
    simple: SimpleHeaderDesktop,
  };

  const HeaderVariantComponent = HeadersVariants[variant ?? ''];

  return HeaderVariantComponent ? <HeaderVariantComponent {...props} /> : <HeaderDesktop {...props} />;
};

export const HeaderNavigationMobileComponent: React.FC<NavigationHeaderProps & HeaderMobileProps> = ({
  variant,
  ...props
}) => {
  const HeadersVariants = {
    simple: SimpleHeaderMobile,
  };

  const HeaderVariantComponent = HeadersVariants[variant ?? ''];

  return HeaderVariantComponent ? <HeaderVariantComponent {...props} /> : <HeaderMobile {...props} />;
};

export const NavigationHeader = ({
  expandedHeaderDisplayAd,
  hideBanner = false,
  hideMenuBar = false,
  hideQuoteBar,
  hideSearchBar,
  hideTopBar,
  initialBreakingsNews,
  initialTrendingTickers,
  initialTrendingTopics,
  isMainDrawerVisible,
  logoVariant,
  marketTickers = DefaultQuotesList,
  menus,
  moneyBannerLink = '',
  moneyBannerText = '',
  onSearch,
  primaryQuotesLabel,
  quotes: initialQuotes,
  showHeaderDisplayAd,
  showRotatingBanner = false,
  sticky = true,
  variant: headerVariant = 'default',
}: NavigationHeaderProps) => {
  const [recentTickers] = useRecentTickers();
  // `pending` keeps SSR + first paint dual-mount (CSS hides the wrong one) to avoid hydration
  // mismatch, then we exclusively mount one header to prevent duplicate #navigation-header.
  const [viewport, setViewport] = React.useState<'mobile' | 'desktop' | 'pending'>('pending');
  const [breakingNews, setBreakingNews] = React.useState<BreakingNews[]>(initialBreakingsNews ?? []);
  const [isBannerShowing, setIsBannerShowing] = React.useState<boolean>(!!initialBreakingsNews?.length);
  const [quotes, setQuotes] = React.useState<Record<StockSymbol, DelayedQuote> | undefined | null>(initialQuotes);
  const session = React.useContext(SessionContext);
  const quotesManager = session.getManager(QuotesManager);
  const shouldShowHeaderDisplayAd = !!showHeaderDisplayAd;
  const headerDisplayAdExpanded = !!expandedHeaderDisplayAd;

  React.useLayoutEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 799px)');
    const syncViewport = () => {
      setViewport(mediaQuery.matches ? 'mobile' : 'desktop');
    };
    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => {
      mediaQuery.removeEventListener('change', syncViewport);
    };
  }, []);

  const isMobileHeaderVisible = viewport === 'pending' || viewport === 'mobile';
  const isDesktopHeaderVisible = viewport === 'pending' || viewport === 'desktop';

  React.useEffect(() => {
    if (hideQuoteBar) {
      return;
    }
    if (initialQuotes) {
      startTransition(() => {
        setQuotes(current => ({ ...(current ?? {}), ...initialQuotes }));
      });
      const coveredSymbols = new Set(Object.keys(initialQuotes));
      const uncoveredTickers = [...marketTickers, ...recentTickers].filter(ticker => !coveredSymbols.has(ticker));
      if (uncoveredTickers.length > 0) {
        quotesManager.getDelayedQuotes(uncoveredTickers).then(res => {
          if (res?.ok) {
            startTransition(() => {
              setQuotes(current => ({ ...(current ?? {}), ...res.ok }));
            });
          }
        });
      }
      return;
    }
    if (!marketTickers) {
      return;
    }
    quotesManager.getDelayedQuotes([...marketTickers, ...recentTickers]).then(res => {
      if (res?.ok) {
        startTransition(() => {
          setQuotes(res.ok);
        });
      }
    });
  }, [quotesManager, marketTickers, initialQuotes, recentTickers, hideQuoteBar]);

  React.useEffect(() => {
    if (hideBanner) return;

    const dateNowTime = DateTime.now().setZone('America/New_York');

    const getCurrentBreakingNews = (breakingNews: BreakingNews[]): BreakingNews[] => {
      if (!Array.isArray(breakingNews)) return [];
      return breakingNews.filter(breakingNewsItem => {
        const formattedTimeEnd = Number(breakingNewsItem.time_end);
        const timeEndDateTime = DateTime.fromSeconds(formattedTimeEnd);
        return dateNowTime < timeEndDateTime;
      });
    };

    const currentBreakingNews = initialBreakingsNews ? getCurrentBreakingNews(initialBreakingsNews) : [];

    if (currentBreakingNews.length > 0) {
      setBreakingNews(currentBreakingNews);
      setIsBannerShowing(true);
      return;
    }

    session
      .getManager(BasicNewsManager)
      .getBreakingNewsFormatted()
      .then(res => {
        const events = res.ok ?? [];
        if (events.length === 0) return;
        setBreakingNews(events);
        setIsBannerShowing(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBreakingsNews, hideBanner]);

  const isMoneyApp = appEnvironment().isApp(appName.money);

  return (
    <div className={`header-container ${isMoneyApp ? 'money-app' : ''}`}>
      {/* {showAboveHeaderBlock && <AboveHeaderBlock />} */}
      {isMobileHeaderVisible && (
        <div className="mobile-header-wrapper">
          <HeaderNavigationMobileComponent
            breakingNews={isBannerShowing && !hideBanner ? breakingNews : []}
            expandedHeaderDisplayAd={headerDisplayAdExpanded}
            featuredMenu={featuredMenu}
            hideMenuBar={hideMenuBar}
            hideQuoteBar={hideQuoteBar}
            hideSearchBar={hideSearchBar}
            initialTrendingTickers={initialTrendingTickers}
            initialTrendingTopics={initialTrendingTopics}
            isActiveHeader={viewport === 'mobile'}
            isMainDrawerVisible={isMainDrawerVisible}
            logoVariant={logoVariant}
            marketTickers={marketTickers}
            menus={menus}
            primaryQuotesLabel={primaryQuotesLabel}
            quotes={quotes}
            shouldRenderHeaderDisplayAd={shouldLoadHeaderDisplayAd(viewport, shouldShowHeaderDisplayAd)}
            showHeaderDisplayAd={shouldShowHeaderDisplayAd}
            showRotatingBanner={showRotatingBanner}
            variant={headerVariant}
            //AboveHeaderBlock={showAboveHeaderBlock ? <AboveHeaderBlock /> : null}
          />
        </div>
      )}
      {isDesktopHeaderVisible && (
        <HeaderNavigationComponent
          breakingNews={isBannerShowing && !hideBanner ? breakingNews : []}
          expandedHeaderDisplayAd={headerDisplayAdExpanded}
          hideBanner={hideBanner}
          hideMenuBar={hideMenuBar}
          hideQuoteBar={hideQuoteBar}
          hideSearchBar={hideSearchBar}
          hideTopBar={hideTopBar}
          initialTrendingTickers={initialTrendingTickers}
          initialTrendingTopics={initialTrendingTopics}
          isActiveHeader={viewport === 'desktop'}
          isMainDrawerVisible={isMainDrawerVisible}
          logoVariant={logoVariant}
          marketTickers={marketTickers}
          menus={menus}
          moneyBannerLink={moneyBannerLink}
          moneyBannerText={moneyBannerText}
          onSearch={onSearch}
          primaryQuotesLabel={primaryQuotesLabel}
          quotes={quotes}
          recentTickers={recentTickers}
          shouldRenderHeaderDisplayAd={shouldLoadHeaderDisplayAd(viewport, shouldShowHeaderDisplayAd)}
          showHeaderDisplayAd={shouldShowHeaderDisplayAd}
          showRotatingBanner={showRotatingBanner}
          sticky={sticky}
          variant={headerVariant}
          //showAboveHeaderBlock={showAboveHeaderBlock}
        />
      )}
    </div>
  );
};

// const AboveHeaderBlock: React.FC = () => {
//   return (
//     <AboveHeaderBlockWrapper>
//       <AboveHeaderAdUnit />
//     </AboveHeaderBlockWrapper>
//   );
// };

// const AboveHeaderBlockWrapper = styled.div`
//   vertical-align: middle;
//   justify-content: center;
//   display: flex;
//   border-bottom: solid 1px #ddd;
// `;

export const AboveHeaderAdUnit = () => {
  return (
    <div className="above-header-ad-unit-wrapper">
      {/* <React.Suspense fallback={<div className="h-[90px]" />}>
        <InvestingChannel
          className="mb-0 above-header-ad-unit"
          id="above-header"
          mobileSize="300x100,300x50,320x100,320x50"
          sensor={false}
          size="728x90,970x90"
        />
      </React.Suspense> */}
    </div>
  );
};
